import type {
	ArtistGenerationPricingRequest,
	ArtistContinentMediaPreflightError as PackageArtistContinentMediaPreflightError,
	ArtistContinentMediaPreflightRequest as PackageArtistContinentMediaPreflightRequest,
	ArtistContinentMediaPreflightResult as PackageArtistContinentMediaPreflightResult,
	ArtistDomainDescriptor as PackageArtistDomainDescriptor,
	ArtistParsedLyrics as PackageArtistParsedLyrics,
	ArtistSyncedLyricLine as PackageArtistSyncedLyricLine,
	ArtistSyncedLyricWord as PackageArtistSyncedLyricWord,
} from "@xy-devmode/artist-domain";
import {
	KMP_ARTIFACT_API_VERSION,
	KMP_ARTIFACT_CONTRACT_DIGEST,
	KMP_ARTIFACT_CONTRACT_VERSION,
	KMP_ARTIFACT_PACKAGE_NAME,
} from "@/contracts/kmpArtifactContract";

export const ARTIST_DOMAIN_PACKAGE_NAME = KMP_ARTIFACT_PACKAGE_NAME;
export const ACCEPTED_ARTIST_DOMAIN_API_VERSION = KMP_ARTIFACT_API_VERSION;
export const ACCEPTED_ARTIST_DOMAIN_CONTRACT_VERSION =
	KMP_ARTIFACT_CONTRACT_VERSION;
export const ACCEPTED_ARTIST_DOMAIN_CONTRACT_DIGEST =
	KMP_ARTIFACT_CONTRACT_DIGEST;

export type ArtistDomainDescriptor = PackageArtistDomainDescriptor;
export type ArtistSyncedLyricWord = PackageArtistSyncedLyricWord;
export type ArtistSyncedLyricLine = PackageArtistSyncedLyricLine;
export type ArtistParsedLyrics = PackageArtistParsedLyrics;
export type ArtistGenerationPricingInput = ArtistGenerationPricingRequest;
export type ArtistContinentMediaPreflightInput =
	PackageArtistContinentMediaPreflightRequest;
export type ArtistContinentMediaPreflightError =
	PackageArtistContinentMediaPreflightError;
export type ArtistContinentMediaPreflightResult =
	PackageArtistContinentMediaPreflightResult;

interface KotlinArtistDomainModule {
	describe(): ArtistDomainDescriptor;
	sanitizeLyrics(text: string): string;
	sanitizeOtpCode(rawValue: string): string;
	canonicalizeEmail(rawValue: string): unknown;
	canConnect(
		sourceAssetKind: string,
		targetAssetKind: string,
	): boolean | null | undefined;
	parseLyrics(
		text: string,
		filename?: string | null,
	): KotlinParsedLyrics | null | undefined;
	generationCredits(
		request: ArtistGenerationPricingInput,
	): number | null | undefined;
	continentCanAdd(existingCount: number): unknown;
	continentTrackRequirement(source: string, continentCount: number): unknown;
	continentMediaPreflight(request: ArtistContinentMediaPreflightInput): unknown;
}

interface KotlinParsedLyrics {
	readonly plain: string | null | undefined;
	readonly synced: readonly KotlinSyncedLyricLine[] | null | undefined;
}

interface KotlinSyncedLyricLine {
	readonly timestamp: number;
	readonly end: number | null | undefined;
	readonly text: string;
	readonly words: readonly KotlinSyncedLyricWord[] | null | undefined;
}

interface KotlinSyncedLyricWord {
	readonly start: number;
	readonly end: number;
	readonly text: string;
}

export interface ArtistDomainRuntime {
	readonly descriptor: Readonly<ArtistDomainDescriptor>;
	sanitizeLyrics(text: string): string;
	sanitizeOtpCode(rawValue: string): string;
	canonicalizeEmail(rawValue: string): ArtistAuthEmailCanonicalizationResult;
	canConnect(sourceAssetKind: string, targetAssetKind: string): boolean | null;
	parseLyrics(
		text: string,
		filename?: string | null,
	): ArtistParsedLyrics | null;
	generationCredits(input: ArtistGenerationPricingInput): number | null;
	continentCanAdd(existingCount: number): ArtistContinentCanAddResult;
	continentTrackRequirement(
		source: string,
		continentCount: number,
	): ArtistContinentTrackRequirementResult;
	continentMediaPreflight(
		input: ArtistContinentMediaPreflightInput,
	): ArtistContinentMediaPreflightResult;
}

export type ArtistAuthEmailCanonicalizationErrorCode =
	| "auth.email.empty"
	| "auth.email.too-long"
	| "auth.email.invalid-characters"
	| "auth.email.invalid-format";

export interface ArtistAuthEmailCanonicalizationError {
	readonly code: ArtistAuthEmailCanonicalizationErrorCode;
	readonly category: "validation";
	readonly retryable: false;
	readonly messageKey:
		| "auth.email.validation.empty"
		| "auth.email.validation.too-long"
		| "auth.email.validation.invalid-characters"
		| "auth.email.validation.invalid-format";
	readonly context: Readonly<Record<string, never>>;
}

export type ArtistAuthEmailCanonicalizationResult =
	| {
			readonly kind: "success";
			readonly value: { readonly canonical: string };
	  }
	| {
			readonly kind: "failure";
			readonly error: ArtistAuthEmailCanonicalizationError;
	  };

export interface ArtistContinentValidationError {
	readonly code:
		| "continent.invalid-count"
		| "continent.max-continents-exceeded"
		| "continent.required"
		| "continent.unknown-source";
	readonly category: "validation";
	readonly retryable: false;
	readonly messageKey:
		| "continent.validation.invalid-count"
		| "continent.validation.max-continents-exceeded"
		| "continent.validation.required"
		| "continent.validation.unknown-source";
	readonly context: Readonly<Record<string, JsonBoundaryValue>>;
}

export type ArtistContinentCanAddResult =
	| { readonly kind: "success"; readonly value: { readonly canAdd: true } }
	| {
			readonly kind: "failure";
			readonly error: ArtistContinentValidationError;
	  };

export type ArtistContinentTrackRequirementResult =
	| {
			readonly kind: "success";
			readonly value: { readonly requirementSatisfied: true };
	  }
	| {
			readonly kind: "failure";
			readonly error: ArtistContinentValidationError;
	  };

type JsonBoundaryValue =
	| null
	| boolean
	| number
	| string
	| JsonBoundaryValue[]
	| { [key: string]: JsonBoundaryValue };

export type ArtistDomainLoader = () => Promise<unknown>;

const vendoredArtistDomainLoader: ArtistDomainLoader = () =>
	import("@xy-devmode/artist-domain");

export class ArtistDomainModuleShapeError extends Error {
	constructor() {
		super(
			`${ARTIST_DOMAIN_PACKAGE_NAME} does not expose the locked Kotlin/JS API shape`,
		);
		this.name = "ArtistDomainModuleShapeError";
	}
}

export class ArtistDomainDescriptorMismatchError extends Error {
	constructor(readonly field: keyof ArtistDomainDescriptor) {
		super(
			`${ARTIST_DOMAIN_PACKAGE_NAME} descriptor field ${field} does not match the accepted artifact`,
		);
		this.name = "ArtistDomainDescriptorMismatchError";
	}
}

export class ArtistDomainRuntimeContractError extends Error {
	constructor(
		readonly method: keyof Omit<ArtistDomainRuntime, "descriptor">,
		options?: ErrorOptions,
	) {
		super(
			`${ARTIST_DOMAIN_PACKAGE_NAME} returned an invalid result from ${method}`,
			options,
		);
		this.name = "ArtistDomainRuntimeContractError";
	}
}

export async function loadArtistDomain(
	loader: ArtistDomainLoader = vendoredArtistDomainLoader,
): Promise<ArtistDomainRuntime> {
	const module = extractKotlinModule(await loader());
	const descriptor = module.describe();
	assertDescriptor(descriptor);

	return {
		descriptor: Object.freeze({
			packageName: descriptor.packageName,
			artifactApiVersion: descriptor.artifactApiVersion,
			contractVersion: descriptor.contractVersion,
			contractDigest: descriptor.contractDigest,
		}),
		sanitizeLyrics: (text) =>
			invokeRuntime("sanitizeLyrics", () =>
				requiredRuntimeString(module.sanitizeLyrics(text)),
			),
		sanitizeOtpCode: (rawValue) =>
			invokeRuntime("sanitizeOtpCode", () =>
				requiredRuntimeString(module.sanitizeOtpCode(rawValue)),
			),
		canonicalizeEmail: (rawValue) =>
			invokeRuntime("canonicalizeEmail", () =>
				normalizeAuthEmailResult(module.canonicalizeEmail(rawValue)),
			),
		canConnect: (sourceAssetKind, targetAssetKind) =>
			invokeRuntime("canConnect", () =>
				normalizeBoolean(module.canConnect(sourceAssetKind, targetAssetKind)),
			),
		parseLyrics: (text, filename = null) =>
			invokeRuntime("parseLyrics", () =>
				normalizeParsedLyrics(module.parseLyrics(text, filename)),
			),
		generationCredits: (input) =>
			invokeRuntime("generationCredits", () =>
				normalizeCredits(module.generationCredits(input)),
			),
		continentCanAdd: (existingCount) =>
			invokeRuntime("continentCanAdd", () => {
				const inputCount = requiredSignedInt32(existingCount);
				return normalizeContinentCanAdd(
					module.continentCanAdd(inputCount),
					inputCount,
				);
			}),
		continentTrackRequirement: (source, continentCount) =>
			invokeRuntime("continentTrackRequirement", () => {
				const inputSource = requiredRuntimeString(source);
				const inputCount = requiredSignedInt32(continentCount);
				return normalizeContinentTrackRequirement(
					module.continentTrackRequirement(inputSource, inputCount),
					inputSource,
					inputCount,
				);
			}),
		continentMediaPreflight: (input) =>
			invokeRuntime("continentMediaPreflight", () => {
				const normalizedInput = normalizeMediaPreflightInput(input);
				return normalizeMediaPreflightResult(
					module.continentMediaPreflight(normalizedInput),
					normalizedInput,
				);
			}),
	};
}

function invokeRuntime<T>(
	method: keyof Omit<ArtistDomainRuntime, "descriptor">,
	action: () => T,
): T {
	try {
		return action();
	} catch (error) {
		if (error instanceof ArtistDomainRuntimeContractError) throw error;
		throw new ArtistDomainRuntimeContractError(method, { cause: error });
	}
}

function extractKotlinModule(moduleValue: unknown): KotlinArtistDomainModule {
	const direct = publicModule(moduleValue);
	if (direct !== null) return direct;
	if (isRecord(moduleValue)) {
		const fromDefault = publicModule(moduleValue.default);
		if (fromDefault !== null) return fromDefault;
	}
	throw new ArtistDomainModuleShapeError();
}

function publicModule(moduleValue: unknown): KotlinArtistDomainModule | null {
	if (!isRecord(moduleValue)) return null;
	for (const method of [
		"describe",
		"sanitizeLyrics",
		"sanitizeOtpCode",
		"canonicalizeEmail",
		"canConnect",
		"parseLyrics",
		"generationCredits",
		"continentCanAdd",
		"continentTrackRequirement",
		"continentMediaPreflight",
	] as const) {
		if (typeof moduleValue[method] !== "function") return null;
	}
	return moduleValue as unknown as KotlinArtistDomainModule;
}

function normalizeAuthEmailResult(
	value: unknown,
): ArtistAuthEmailCanonicalizationResult {
	if (!isRecord(value)) throw new TypeError("email result must be an object");
	assertExactKeys(
		value,
		value.kind === "success" ? ["kind", "value"] : ["kind", "error"],
	);
	if (value.kind === "success") {
		if (!isRecord(value.value)) {
			throw new TypeError("email success value must be an object");
		}
		assertExactKeys(value.value, ["canonical"]);
		const canonical = requiredRuntimeString(value.value.canonical);
		if (
			canonical.length < 6 ||
			canonical.length > 254 ||
			!/^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/u.test(
				canonical,
			)
		) {
			throw new TypeError("email canonical value is invalid");
		}
		return { kind: "success", value: { canonical } };
	}
	if (value.kind !== "failure") {
		throw new TypeError("email result kind is unsupported");
	}
	if (!isRecord(value.error)) {
		throw new TypeError("email failure error must be an object");
	}
	assertExactKeys(value.error, [
		"code",
		"category",
		"retryable",
		"messageKey",
		"context",
	]);
	const code = value.error.code;
	if (
		!isAuthEmailErrorCode(code) ||
		value.error.category !== "validation" ||
		value.error.retryable !== false ||
		value.error.messageKey !== emailMessageKey(code) ||
		!isRecord(value.error.context)
	) {
		throw new TypeError("email failure metadata is invalid");
	}
	assertExactKeys(value.error.context, []);
	return {
		kind: "failure",
		error: {
			code,
			category: "validation",
			retryable: false,
			messageKey: emailMessageKey(code),
			context: {},
		},
	};
}

function isAuthEmailErrorCode(
	value: unknown,
): value is ArtistAuthEmailCanonicalizationErrorCode {
	return (
		value === "auth.email.empty" ||
		value === "auth.email.too-long" ||
		value === "auth.email.invalid-characters" ||
		value === "auth.email.invalid-format"
	);
}

function emailMessageKey(
	code: ArtistAuthEmailCanonicalizationErrorCode,
): ArtistAuthEmailCanonicalizationError["messageKey"] {
	switch (code) {
		case "auth.email.empty":
			return "auth.email.validation.empty";
		case "auth.email.too-long":
			return "auth.email.validation.too-long";
		case "auth.email.invalid-characters":
			return "auth.email.validation.invalid-characters";
		case "auth.email.invalid-format":
			return "auth.email.validation.invalid-format";
	}
}

function assertDescriptor(value: ArtistDomainDescriptor): void {
	if (!isRecord(value)) throw new ArtistDomainModuleShapeError();
	const expected: ArtistDomainDescriptor = {
		packageName: ARTIST_DOMAIN_PACKAGE_NAME,
		artifactApiVersion: ACCEPTED_ARTIST_DOMAIN_API_VERSION,
		contractVersion: ACCEPTED_ARTIST_DOMAIN_CONTRACT_VERSION,
		contractDigest: ACCEPTED_ARTIST_DOMAIN_CONTRACT_DIGEST,
	};
	if (
		JSON.stringify(Object.keys(value).toSorted()) !==
		JSON.stringify(Object.keys(expected).toSorted())
	) {
		throw new ArtistDomainModuleShapeError();
	}
	for (const field of Object.keys(
		expected,
	) as (keyof ArtistDomainDescriptor)[]) {
		if (value[field] !== expected[field])
			throw new ArtistDomainDescriptorMismatchError(field);
	}
}

function normalizeParsedLyrics(
	value: KotlinParsedLyrics | null | undefined,
): ArtistParsedLyrics | null {
	if (value == null) return null;
	if (!isRecord(value)) throw new TypeError("parsed lyrics must be an object");
	const plain = nullableRuntimeString(value.plain);
	const synced = normalizeSyncedLines(value.synced);
	return {
		plain,
		synced,
	};
}

function normalizeSyncedLines(
	value: unknown,
): readonly ArtistSyncedLyricLine[] | null {
	if (value == null) return null;
	if (!Array.isArray(value) || value.length === 0) {
		throw new TypeError("synced lyrics must be a non-empty array");
	}
	return value.map((line) => {
		if (!isRecord(line)) throw new TypeError("synced line must be an object");
		const timestamp = nonNegativeFiniteNumber(line.timestamp);
		const end = nullableNonNegativeFiniteNumber(line.end);
		const text = requiredRuntimeString(line.text);
		if (text.length === 0) throw new TypeError("synced line text is required");
		const words = normalizeSyncedWords(line.words);
		return { timestamp, end, text, words };
	});
}

function normalizeSyncedWords(
	value: unknown,
): readonly ArtistSyncedLyricWord[] | null {
	if (value == null) return null;
	if (!Array.isArray(value) || value.length === 0) {
		throw new TypeError("synced words must be a non-empty array");
	}
	return value.map((word) => {
		if (!isRecord(word)) throw new TypeError("synced word must be an object");
		const start = nonNegativeFiniteNumber(word.start);
		const end = nonNegativeFiniteNumber(word.end);
		if (end < start) throw new TypeError("synced word end precedes start");
		const text = requiredRuntimeString(word.text);
		if (text.length === 0) throw new TypeError("synced word text is required");
		return { start, end, text };
	});
}

function requiredRuntimeString(value: unknown): string {
	if (typeof value !== "string") throw new TypeError("expected a string");
	return value;
}

function nullableRuntimeString(value: unknown): string | null {
	if (value == null) return null;
	return requiredRuntimeString(value);
}

function normalizeBoolean(value: unknown): boolean | null {
	if (value == null) return null;
	if (typeof value !== "boolean") throw new TypeError("expected a boolean");
	return value;
}

function normalizeCredits(value: unknown): number | null {
	if (value == null) return null;
	if (!Number.isSafeInteger(value) || (value as number) < 0) {
		throw new TypeError("credits must be a non-negative safe integer");
	}
	return value as number;
}

function normalizeContinentCanAdd(
	value: unknown,
	existingCount: number,
): ArtistContinentCanAddResult {
	if (!isRecord(value))
		throw new TypeError("continent result must be an object");
	assertExactKeys(
		value,
		value.kind === "success" ? ["kind", "value"] : ["kind", "error"],
	);
	if (value.kind === "success") {
		if (!isRecord(value.value))
			throw new TypeError("continent success value must be an object");
		assertExactKeys(value.value, ["canAdd"]);
		if (value.value.canAdd !== true)
			throw new TypeError("canAdd success must be true");
		return { kind: "success", value: { canAdd: true } };
	}
	if (value.kind !== "failure")
		throw new TypeError("continent result kind is unsupported");
	return {
		kind: "failure",
		error: normalizeContinentError(value.error, "can-add", existingCount, null),
	};
}

function normalizeContinentTrackRequirement(
	value: unknown,
	source: string,
	continentCount: number,
): ArtistContinentTrackRequirementResult {
	if (!isRecord(value))
		throw new TypeError("continent result must be an object");
	assertExactKeys(
		value,
		value.kind === "success" ? ["kind", "value"] : ["kind", "error"],
	);
	if (value.kind === "success") {
		if (!isRecord(value.value))
			throw new TypeError("continent success value must be an object");
		assertExactKeys(value.value, ["requirementSatisfied"]);
		if (value.value.requirementSatisfied !== true) {
			throw new TypeError("requirementSatisfied success must be true");
		}
		return { kind: "success", value: { requirementSatisfied: true } };
	}
	if (value.kind !== "failure")
		throw new TypeError("continent result kind is unsupported");
	return {
		kind: "failure",
		error: normalizeContinentError(
			value.error,
			"track-requirement",
			continentCount,
			source,
		),
	};
}

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp"] as const;
const VIDEO_EXTENSIONS = ["mp4", "mov"] as const;
const IMAGE_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
const VIDEO_MIME_TYPES = ["video/mp4", "video/quicktime"] as const;

function normalizeMediaPreflightInput(
	value: ArtistContinentMediaPreflightInput,
): ArtistContinentMediaPreflightInput {
	if (!isRecord(value)) throw new TypeError("media input must be an object");
	assertExactKeys(value, [
		"mediaType",
		"filename",
		"mimeType",
		"byteCount",
		"durationMilliseconds",
	]);
	const mediaType = requiredRuntimeString(value.mediaType);
	const filename = requiredRuntimeString(value.filename);
	if (mediaType.length === 0 || filename.length === 0) {
		throw new TypeError("mediaType and filename must be non-empty strings");
	}
	return {
		mediaType,
		filename,
		mimeType: nullableStrictString(value.mimeType),
		byteCount: nullableSafeInteger(value.byteCount),
		durationMilliseconds: nullableSafeInteger(value.durationMilliseconds),
	};
}

function normalizeMediaPreflightResult(
	value: unknown,
	input: ArtistContinentMediaPreflightInput,
): ArtistContinentMediaPreflightResult {
	if (!isRecord(value)) throw new TypeError("media result must be an object");
	assertExactKeys(
		value,
		value.kind === "success" ? ["kind", "value"] : ["kind", "error"],
	);
	if (value.kind === "success") {
		if (!isRecord(value.value))
			throw new TypeError("media success value must be an object");
		assertExactKeys(value.value, [
			"accepted",
			"mediaType",
			"fileExtension",
			"mimeType",
		]);
		const mediaType = value.value.mediaType;
		const fileExtension = value.value.fileExtension;
		const mimeType = value.value.mimeType;
		const allowedExtensions =
			mediaType === "image"
				? IMAGE_EXTENSIONS
				: mediaType === "video"
					? VIDEO_EXTENSIONS
					: null;
		const allowedMimeTypes =
			mediaType === "image"
				? IMAGE_MIME_TYPES
				: mediaType === "video"
					? VIDEO_MIME_TYPES
					: null;
		if (
			value.value.accepted !== true ||
			mediaType !== input.mediaType ||
			allowedExtensions === null ||
			typeof fileExtension !== "string" ||
			!includesString(allowedExtensions, fileExtension) ||
			(mimeType !== null &&
				(typeof mimeType !== "string" ||
					allowedMimeTypes === null ||
					!includesString(allowedMimeTypes, mimeType))) ||
			(mimeType !== null && mimeType !== expectedMediaMimeType(fileExtension))
		) {
			throw new TypeError("media success does not satisfy the result schema");
		}
		return {
			kind: "success",
			value: {
				accepted: true,
				mediaType: mediaType as "image" | "video",
				fileExtension: fileExtension as
					| "jpg"
					| "jpeg"
					| "png"
					| "webp"
					| "mp4"
					| "mov",
				mimeType: mimeType as
					| "image/jpeg"
					| "image/png"
					| "image/webp"
					| "video/mp4"
					| "video/quicktime"
					| null,
			},
		};
	}
	if (value.kind !== "failure")
		throw new TypeError("media result kind is unsupported");
	return {
		kind: "failure",
		error: normalizeMediaPreflightError(value.error, input),
	};
}

function normalizeMediaPreflightError(
	value: unknown,
	input: ArtistContinentMediaPreflightInput,
): ArtistContinentMediaPreflightError {
	if (!isRecord(value) || !isRecord(value.context)) {
		throw new TypeError("media failure must contain an error context");
	}
	assertExactKeys(value, [
		"code",
		"category",
		"retryable",
		"messageKey",
		"context",
	]);
	if (
		value.category !== "validation" ||
		value.retryable !== false ||
		typeof value.code !== "string" ||
		value.messageKey !==
			`continent.media.validation.${value.code.slice("continent.media.".length)}`
	) {
		throw new TypeError("media failure metadata is invalid");
	}
	const context = value.context;
	switch (value.code) {
		case "continent.media.unsupported-media-type":
			assertExactKeys(context, ["mediaType", "allowedMediaTypes"]);
			if (
				context.mediaType !== input.mediaType ||
				!sameStringArray(context.allowedMediaTypes, ["image", "video"])
			) {
				throw new TypeError("unsupported media type context is invalid");
			}
			break;
		case "continent.media.metadata-unavailable":
			assertExactKeys(context, ["field"]);
			if (context.field !== "byteCount" || input.byteCount !== null) {
				throw new TypeError("missing media metadata context is invalid");
			}
			break;
		case "continent.media.negative-size":
			assertExactKeys(context, ["byteCount", "minimum"]);
			if (
				context.byteCount !== input.byteCount ||
				typeof context.byteCount !== "number" ||
				context.byteCount >= 0 ||
				context.minimum !== 0
			) {
				throw new TypeError("negative media size context is invalid");
			}
			break;
		case "continent.media.empty-payload":
			assertExactKeys(context, ["byteCount", "minimum"]);
			if (
				context.byteCount !== 0 ||
				input.byteCount !== 0 ||
				context.minimum !== 1
			) {
				throw new TypeError("empty media payload context is invalid");
			}
			break;
		case "continent.media.file-too-large": {
			assertExactKeys(context, [
				"byteCount",
				"maximumBytes",
				"maximumMegabytes",
			]);
			const image = input.mediaType === "image";
			const maximumBytes = image ? 10_485_760 : 52_428_800;
			if (
				(input.mediaType !== "image" && input.mediaType !== "video") ||
				context.byteCount !== input.byteCount ||
				typeof context.byteCount !== "number" ||
				context.byteCount <= maximumBytes ||
				context.maximumBytes !== maximumBytes ||
				context.maximumMegabytes !== (image ? 10 : 50)
			) {
				throw new TypeError("media size limit context is invalid");
			}
			break;
		}
		case "continent.media.invalid-extension": {
			assertExactKeys(context, [
				"mediaType",
				"fileExtension",
				"allowedExtensions",
			]);
			const allowed =
				input.mediaType === "image" ? IMAGE_EXTENSIONS : VIDEO_EXTENSIONS;
			if (
				(input.mediaType !== "image" && input.mediaType !== "video") ||
				context.mediaType !== input.mediaType ||
				typeof context.fileExtension !== "string" ||
				includesString(allowed, context.fileExtension) ||
				!sameStringArray(context.allowedExtensions, allowed)
			) {
				throw new TypeError("invalid media extension context is invalid");
			}
			break;
		}
		case "continent.media.invalid-mime": {
			assertExactKeys(context, ["mediaType", "mimeType", "allowedMIMETypes"]);
			const allowed =
				input.mediaType === "image" ? IMAGE_MIME_TYPES : VIDEO_MIME_TYPES;
			if (
				(input.mediaType !== "image" && input.mediaType !== "video") ||
				context.mediaType !== input.mediaType ||
				context.mimeType !== input.mimeType ||
				typeof context.mimeType !== "string" ||
				includesString(allowed, asciiLowercase(context.mimeType)) ||
				!sameStringArray(context.allowedMIMETypes, allowed)
			) {
				throw new TypeError("invalid media MIME context is invalid");
			}
			break;
		}
		case "continent.media.mime-extension-mismatch":
			assertExactKeys(context, [
				"fileExtension",
				"mimeType",
				"expectedMIMEType",
			]);
			if (
				typeof context.fileExtension !== "string" ||
				typeof context.mimeType !== "string" ||
				typeof input.mimeType !== "string" ||
				context.mimeType !== asciiLowercase(input.mimeType) ||
				context.expectedMIMEType !==
					expectedMediaMimeType(context.fileExtension) ||
				context.mimeType === context.expectedMIMEType
			) {
				throw new TypeError("media MIME pairing context is invalid");
			}
			break;
		case "continent.media.video-duration-missing":
			assertExactKeys(context, ["field"]);
			if (
				input.mediaType !== "video" ||
				input.durationMilliseconds !== null ||
				context.field !== "durationMilliseconds"
			) {
				throw new TypeError("missing video duration context is invalid");
			}
			break;
		case "continent.media.video-duration-invalid":
			assertExactKeys(context, ["durationMilliseconds", "minimum"]);
			if (
				input.mediaType !== "video" ||
				context.durationMilliseconds !== input.durationMilliseconds ||
				typeof context.durationMilliseconds !== "number" ||
				context.durationMilliseconds > 0 ||
				context.minimum !== 1
			) {
				throw new TypeError("invalid video duration context is invalid");
			}
			break;
		case "continent.media.video-duration-exceeded":
			assertExactKeys(context, ["durationMilliseconds", "maximumMilliseconds"]);
			if (
				input.mediaType !== "video" ||
				context.durationMilliseconds !== input.durationMilliseconds ||
				typeof context.durationMilliseconds !== "number" ||
				context.durationMilliseconds <= 20_000 ||
				context.maximumMilliseconds !== 20_000
			) {
				throw new TypeError("exceeded video duration context is invalid");
			}
			break;
		default:
			throw new TypeError("media failure code is unsupported");
	}
	return value as unknown as ArtistContinentMediaPreflightError;
}

function expectedMediaMimeType(extension: string): string {
	switch (extension) {
		case "jpg":
		case "jpeg":
			return "image/jpeg";
		case "png":
			return "image/png";
		case "webp":
			return "image/webp";
		case "mp4":
			return "video/mp4";
		case "mov":
			return "video/quicktime";
		default:
			return "";
	}
}

function includesString(values: readonly string[], value: string): boolean {
	return values.some((candidate) => candidate === value);
}

function sameStringArray(value: unknown, expected: readonly string[]): boolean {
	return (
		Array.isArray(value) &&
		value.every((item) => typeof item === "string") &&
		JSON.stringify(value) === JSON.stringify(expected)
	);
}

function asciiLowercase(value: string): string {
	let normalized = "";
	for (const character of value) {
		const code = character.charCodeAt(0);
		normalized +=
			code >= 65 && code <= 90 ? String.fromCharCode(code + 32) : character;
	}
	return normalized;
}

function nullableStrictString(value: unknown): string | null {
	if (value === null) return null;
	return requiredRuntimeString(value);
}

function nullableSafeInteger(value: unknown): number | null {
	if (value === null) return null;
	if (typeof value !== "number" || !Number.isSafeInteger(value)) {
		throw new TypeError("expected a safe integer or null");
	}
	return value;
}

function normalizeContinentError(
	value: unknown,
	operation: "can-add" | "track-requirement",
	expectedCount: number,
	expectedSource: string | null,
): ArtistContinentValidationError {
	if (!isRecord(value))
		throw new TypeError("continent failure error must be an object");
	assertExactKeys(value, [
		"code",
		"category",
		"retryable",
		"messageKey",
		"context",
	]);
	if (
		value.category !== "validation" ||
		value.retryable !== false ||
		!isRecord(value.context)
	) {
		throw new TypeError("continent failure metadata is invalid");
	}
	const code = value.code;
	if (code === "continent.invalid-count") {
		const field = operation === "can-add" ? "existingCount" : "continentCount";
		if (value.messageKey !== "continent.validation.invalid-count") {
			throw new TypeError("invalid-count messageKey is invalid");
		}
		assertExactKeys(value.context, ["field", "value", "minimum"]);
		if (
			value.context.field !== field ||
			!isSignedInt32(value.context.value) ||
			(value.context.value as number) >= 0 ||
			value.context.value !== expectedCount ||
			value.context.minimum !== 0
		) {
			throw new TypeError("invalid-count context is invalid");
		}
		return errorValue(code, value.messageKey, {
			field,
			value: value.context.value as number,
			minimum: 0,
		});
	}
	if (code === "continent.max-continents-exceeded" && operation === "can-add") {
		if (value.messageKey !== "continent.validation.max-continents-exceeded") {
			throw new TypeError("maximum messageKey is invalid");
		}
		assertExactKeys(value.context, ["existingCount", "maximumCount"]);
		if (
			!isSignedInt32(value.context.existingCount) ||
			(value.context.existingCount as number) < 3 ||
			value.context.existingCount !== expectedCount ||
			value.context.maximumCount !== 3
		) {
			throw new TypeError("maximum context is invalid");
		}
		return errorValue(code, value.messageKey, {
			existingCount: value.context.existingCount as number,
			maximumCount: 3,
		});
	}
	if (code === "continent.required" && operation === "track-requirement") {
		if (value.messageKey !== "continent.validation.required") {
			throw new TypeError("required messageKey is invalid");
		}
		assertExactKeys(value.context, [
			"source",
			"continentCount",
			"minimumCount",
		]);
		if (
			(value.context.source !== "generated" &&
				value.context.source !== "uploaded") ||
			value.context.source !== expectedSource ||
			!isSignedInt32(value.context.continentCount) ||
			value.context.continentCount !== 0 ||
			value.context.continentCount !== expectedCount ||
			value.context.minimumCount !== 1
		) {
			throw new TypeError("required context is invalid");
		}
		return errorValue(code, value.messageKey, {
			source: value.context.source,
			continentCount: value.context.continentCount as number,
			minimumCount: 1,
		});
	}
	if (
		code === "continent.unknown-source" &&
		operation === "track-requirement"
	) {
		if (value.messageKey !== "continent.validation.unknown-source") {
			throw new TypeError("unknown-source messageKey is invalid");
		}
		assertExactKeys(value.context, [
			"source",
			"allowedSources",
			"caseSensitive",
		]);
		if (
			typeof value.context.source !== "string" ||
			value.context.source !== expectedSource ||
			JSON.stringify(value.context.allowedSources) !==
				JSON.stringify(["generated", "uploaded", "external"]) ||
			value.context.caseSensitive !== true
		) {
			throw new TypeError("unknown-source context is invalid");
		}
		return errorValue(code, value.messageKey, {
			source: value.context.source,
			allowedSources: ["generated", "uploaded", "external"],
			caseSensitive: true,
		});
	}
	throw new TypeError("continent failure code is invalid for this operation");
}

function errorValue(
	code: ArtistContinentValidationError["code"],
	messageKey: ArtistContinentValidationError["messageKey"],
	context: Readonly<Record<string, JsonBoundaryValue>>,
): ArtistContinentValidationError {
	return {
		code,
		category: "validation",
		retryable: false,
		messageKey,
		context,
	};
}

function assertExactKeys(
	value: Record<string, unknown>,
	keys: readonly string[],
): void {
	const actual = Object.keys(value).toSorted();
	const expected = [...keys].toSorted();
	if (JSON.stringify(actual) !== JSON.stringify(expected)) {
		throw new TypeError("KMP object contains missing or unexpected fields");
	}
}

function isSignedInt32(value: unknown): value is number {
	return (
		typeof value === "number" &&
		Number.isInteger(value) &&
		value >= -2_147_483_648 &&
		value <= 2_147_483_647
	);
}

function requiredSignedInt32(value: unknown): number {
	if (!isSignedInt32(value)) throw new TypeError("expected a signed Int32");
	return value;
}

function nonNegativeFiniteNumber(value: unknown): number {
	if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
		throw new TypeError("expected a non-negative finite number");
	}
	return value;
}

function nullableNonNegativeFiniteNumber(value: unknown): number | null {
	if (value == null) return null;
	return nonNegativeFiniteNumber(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}
